import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Filter, Download, Mail, Phone } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import DataTable from '../../components/DataTable';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

import EmployeeCard from './components/EmployeeCard';
import EmployeeFilters from './components/EmployeeFilters';
import DirectoryStats from './components/DirectoryStats';
import { employeesApi, departmentsApi } from '../../lib/api';

const EmployeeDirectory = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    location: '',
    status: '',
    employmentType: ''
  });

  // Load data on component mount
  useEffect(() => {
    loadEmployees();
    loadDepartments();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeesApi?.list(filters);
      setEmployees(data);
    } catch (err) {
      setError(err?.message);
      setEmployees([]); // Empty state for demo
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await departmentsApi?.list();
      setDepartments(data);
    } catch (err) {
      setDepartments([]); // Empty state for demo
    }
  };

  // Filter employees based on search and filters
  const filteredEmployees = employees?.filter(employee => {
    const matchesSearch = !searchTerm || 
      `${employee?.firstName || ''} ${employee?.lastName || ''}`?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      employee?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      employee?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesDepartment = !filters?.department || employee?.departmentId === filters?.department;
    const matchesLocation = !filters?.location || employee?.location === filters?.location;
    const matchesStatus = !filters?.status || employee?.status === filters?.status;
    const matchesEmploymentType = !filters?.employmentType || employee?.employmentType === filters?.employmentType;

    return matchesSearch && matchesDepartment && matchesLocation && matchesStatus && matchesEmploymentType;
  }) || [];

  const handleCreateEmployee = () => {
    // TODO: Open create employee modal
    console.log('Create employee clicked');
  };

  const handleExportDirectory = () => {
    // TODO: Export employee directory
    console.log('Export directory clicked');
  };

  const handleEmployeeClick = (employee) => {
    // TODO: Navigate to employee profile
    console.log('Employee clicked:', employee);
  };

  // Table columns for table view
  const tableColumns = [
    {
      key: 'name',
      label: 'Employee',
      render: (_, employee) => (
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
            <Users className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {employee?.firstName} {employee?.lastName}
            </div>
            <div className="text-sm text-gray-500">{employee?.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'title',
      label: 'Title',
      render: (title) => title || 'No title'
    },
    {
      key: 'department',
      label: 'Department',
      render: (_, employee) => {
        const dept = departments?.find(d => d?.id === employee?.departmentId);
        return dept?.name || 'No department';
      }
    },
    {
      key: 'location',
      label: 'Location',
      render: (location) => location || 'Remote'
    },
    {
      key: 'status',
      label: 'Status',
      render: (status) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          status === 'active' ? 'bg-green-100 text-green-800' :
          status === 'inactive'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {status || 'active'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, employee) => (
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm">
            <Mail className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <Phone className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  const pageHeaderActions = [
    {
      label: 'Export Directory',
      icon: Download,
      variant: 'outline',
      onClick: handleExportDirectory
    },
    {
      label: 'Add Employee',
      icon: Plus,
      onClick: handleCreateEmployee
    }
  ];

  // Show API not connected banner
  const apiNotConnected = error && error?.includes('Not implemented');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Employee Directory"
        subtitle="Manage and view all organizational personnel"
        actions={pageHeaderActions}
        breadcrumbs={[
          { label: 'HR', href: '/' },
          { label: 'Employee Directory' }
        ]}
      />
      {apiNotConnected && (
        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Backend not configured
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Set VITE_API_URL and wire endpoints to enable employee data.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="px-6 py-6">
        {/* Directory Stats */}
        <DirectoryStats 
          employees={filteredEmployees} 
          departments={departments}
        />

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search employees by name, email, or title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <EmployeeFilters
                filters={filters}
                onFiltersChange={setFilters}
                departments={departments}
              />
              
              <div className="flex items-center space-x-2">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                >
                  Table
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Employee List */}
        {filteredEmployees?.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No employees found"
            description="Get started by adding your first employee to the directory or adjust your search filters."
            primaryAction={{
              label: 'Add Employee',
              icon: Plus,
              onClick: handleCreateEmployee
            }}
            secondaryAction={{
              label: 'Import Employees',
              icon: Download,
              onClick: () => console.log('Import employees')
            }}
          />
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredEmployees?.map((employee) => (
                  <EmployeeCard
                    key={employee?.id || Math.random()}
                    employee={employee}
                    onClick={() => handleEmployeeClick(employee)}
                    department={departments?.find(d => d?.id === employee?.departmentId)}
                  />
                ))}
              </div>
            ) : (
              <DataTable
                columns={tableColumns}
                data={filteredEmployees}
                onRowClick={handleEmployeeClick}
                emptyState={
                  <EmptyState
                    icon={Users}
                    title="No employees found"
                    description="No employees match your current search criteria."
                  />
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeDirectory;